import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

/** The shell node overlays anchor to — `PhoneFrame`'s `.app-frame-inner`. */
export const FRAME_OVERLAY_HOST_ID = 'app-overlays'

/**
 * Renders screen-owned overlays (modals, popovers) as children of `.app-frame-inner` rather
 * than of the screen they belong to.
 *
 * `Drawer`, `NudgeSheet` and `InstallPromptSheet` are already shell-level siblings there, so
 * their `position: absolute` resolves against the frame. A screen's own popover cannot be —
 * it lives inside `.app-screen`, which is `position: relative` *and* the scroll container, so
 * `inset: 0` resolves against the scrolled content box and the dialog centers off-screen once
 * the user has scrolled. This portal buys the shell anchoring without hoisting screen-local
 * state into the shell.
 *
 * Falls back to rendering in place when the host is absent — screen tests mount a screen
 * directly into a bare container with no `PhoneFrame` around it.
 */
export function FrameOverlay({ children }: { readonly children: ReactNode }): JSX.Element {
  const host = typeof document === 'undefined' ? null : document.getElementById(FRAME_OVERLAY_HOST_ID)
  return host === null ? <>{children}</> : createPortal(children, host)
}
